//1/ Exam.js

import { useCallback, useEffect, useState } from "react";
import Layout from "../../Components/Website/Layout";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { baseURL, EXAM, SUBMITANSWR } from "../../API/API";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Choise from "../../Components/common/Choise";
import TrueORFalse from "../../Components/common/TrueORFalse";
import Written from "../../Components/common/Written";
import "../Website/Exam.css";
import {
  faArrowLeft,
  faArrowRight,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { useDataRefresh } from "../context/DataRefreshContext";

function normalizeQuestionType(type) {
  const value = String(type || "mcq").toLowerCase();
  if (["truefalse", "true_false", "boolean", "tf"].includes(value)) {
    return "tf";
  }
  if (["multiple_choice", "multiplechoice"].includes(value)) {
    return "mcq";
  }
  if (["essay", "written"].includes(value)) {
    return "text";
  }
  return value || "mcq";
}

function normalizeExamPayload(raw) {
  if (!raw) return { title: "", time_limit_minutes: 30, questions: [] };

  const questions = Array.isArray(raw.questions)
    ? raw.questions
        .filter(Boolean)
        .map((q) => ({
          id: q.id,
          text: q.text ?? q.question ?? "",
          question_type: normalizeQuestionType(q.question_type),
          choices: Array.isArray(q.choices) ? q.choices : [],
          grade: q.grade,
        }))
    : [];

  return {
    id: raw.id,
    title: raw.title ?? raw.name ?? "امتحان الوحدة",
    time_limit_minutes: raw.time_limit_minutes ?? raw.time ?? 30,
    questions,
  };
}

export default function Exam() {
  const axiosPrivate = useAxiosPrivate();
  const { id } = useParams();
  const navigate = useNavigate();
  const AcademacID = useLocation();
  const academicId = AcademacID.state?.id;
  const { triggerRefresh } = useDataRefresh();

  const [examData, setExamData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const questions = examData?.questions ?? [];
  const currentQuestion = questions[currentIndex] ?? null;

  useEffect(() => {
    async function fetchExam() {
      try {
        setLoadError(null);
        const res = await axiosPrivate.get(`${baseURL}/${EXAM}/${id}/`);

        const payload = res.data?.data;
        if (!payload) {
          setLoadError("لم يتم العثور على الامتحان");
          toast.error("لم يتم العثور على الامتحان");
          return;
        }

        const normalized = normalizeExamPayload(payload);
        if (!normalized.questions.length) {
          setLoadError("لا توجد أسئلة في هذا الامتحان");
          toast.error("لا توجد أسئلة في هذا الامتحان");
        }

        setExamData(normalized);
        setCurrentIndex(0);

        if (normalized.time_limit_minutes) {
          setTimeLeft(normalized.time_limit_minutes * 60);
        }
      } catch (error) {
        console.error("فشل تحميل الامتحان:", error);
        const errorMsg =
          error?.response?.status === 404
            ? "الامتحان غير موجود"
            : error?.response?.status === 403
            ? "غير مصرح بالدخول لهذا الامتحان"
            : "فشل تحميل الامتحان";
        setLoadError(errorMsg);
        toast.error(errorMsg);
      }
    }

    if (id) {
      fetchExam();
    }
  }, [id, axiosPrivate]);

  const formatTime = (seconds) => {
    if (seconds == null || Number.isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const handleAnswer = (questionId, answerValue) => {
    setAnswers((prev) => {
      const exists = prev.find((a) => a.questionId === questionId);
      return exists
        ? prev.map((a) =>
            a.questionId === questionId
              ? { questionId, answer: answerValue }
              : a
          )
        : [...prev, { questionId, answer: answerValue }];
    });
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = useCallback(async () => {
    try {
      const payload = {
        exam_id: examData?.id ?? id,
        answers: answers.map((a) => ({
          question_id: a.questionId,
          answer: a.answer,
        })),
      };
      const response = await axiosPrivate.post(
        `${baseURL}/${EXAM}/${id}/${SUBMITANSWR}/`,
        payload
      );

      toast.success(response.data.message || "✅ تم إرسال الامتحان بنجاح");

      triggerRefresh();

      navigate(`/resalt-exam/${id}`, {
        state: {
          examresalt: response.data,
          academacid: academicId,
        },
      });
    } catch (error) {
      console.error("خطأ في إرسال الامتحان:", error);
      const errorMsg =
        error?.response?.data?.message ||
        (error?.response?.status === 404
          ? "الامتحان غير موجود"
          : error?.response?.status === 403
          ? "غير مصرح بتقديم هذا الامتحان"
          : "حدث خطأ أثناء إرسال الامتحان");
      toast.error("❌ " + errorMsg);
    }
  }, [academicId, answers, axiosPrivate, examData?.id, id, navigate, triggerRefresh]);

  useEffect(() => {
    if (timeLeft === null) return;
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [handleSubmit, timeLeft]);

  const renderQuestion = (question) => {
    if (!question || !question.question_type) {
      return (
        <p className="text-muted mb-0">تعذر تحميل هذا السؤال. انتقل للسؤال التالي.</p>
      );
    }

    const userAnswer = answers.find((a) => a.questionId === question.id)?.answer;

    switch (question.question_type) {
      case "mcq":
        return (
          <Choise
            question={question}
            onAnswer={handleAnswer}
            selectedAnswer={userAnswer}
          />
        );
      case "tf":
      case "truefalse":
      case "true_false":
        return (
          <TrueORFalse
            question={question}
            onAnswer={handleAnswer}
            selectedAnswer={userAnswer}
          />
        );
      case "text":
        return (
          <Written
            question={question}
            onAnswer={handleAnswer}
            currentText={userAnswer || ""}
          />
        );
      default:
        return (
          <p className="text-muted mb-0">
            نوع السؤال غير مدعوم: {question.question_type}
          </p>
        );
    }
  };

  if (!examData && !loadError) {
    return (
      <Layout>
        <div className="text-center py-5">جاري تحميل الامتحان...</div>
      </Layout>
    );
  }

  if (loadError || !questions.length) {
    return (
      <Layout>
        <div className="container mt-4 text-center py-5">
          <p className="text-danger fs-5">{loadError || "لا توجد أسئلة"}</p>
          <button
            type="button"
            className="btn-rejster mt-3"
            onClick={() => navigate(academicId ? `/learn/${academicId}` : "/courses")}
          >
            العودة للوحدة
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mt-4 emam-page">
        <div className="exam-header">
          <h4 className="exam-title">{examData.title}</h4>

          <div
            className={`exam-timer ${timeLeft != null && timeLeft <= 300 ? "danger" : "normal"}`}
          >
            <span>{formatTime(timeLeft)}</span>
            <FontAwesomeIcon icon={faClock} style={{ fontSize: "22px" }} />
          </div>
        </div>

        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span style={{ fontWeight: "500", color: "var(--nat-700)" }}>
              التقدم في الامتحان:
            </span>
            <span style={{ fontWeight: "600", color: "var(--brand-300)" }}>
              {Math.round(((currentIndex + 1) / questions.length) * 100)}%
            </span>
          </div>

          <div className="progress" style={{ height: "12px", borderRadius: "6px" }}>
            <div
              className="progress-bar bg-success"
              role="progressbar"
              style={{
                width: `${((currentIndex + 1) / questions.length) * 100}%`,
                transition: "width 0.5s ease-in-out",
              }}
            />
          </div>
        </div>

        <div
          className="mb-4 p-4"
          style={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            direction: "rtl",
          }}
        >
          <h5
            className="mb-3"
            style={{
              color: "#333",
              fontWeight: "700",
              fontSize: "1.2rem",
              borderBottom: "2px solid var(--brand-300)",
              paddingBottom: "8px",
            }}
          >
            <span style={{ color: "var(--brand-300)" }}>السؤال</span>{" "}
            <span style={{ color: "#000" }}>{currentIndex + 1}</span>:
          </h5>

          <div style={{ fontSize: "1.05rem", color: "#444" }}>
            {renderQuestion(currentQuestion)}
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-5 px-2">
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="d-flex align-items-center justify-content-center btn-circle me-2"
          >
            <FontAwesomeIcon icon={faArrowRight} />
          </button>

          <div className="fw-semibold fs-5 text-center text-secondary">
            سؤال {currentIndex + 1} من {questions.length}
          </div>

          <button
            type="button"
            onClick={handleNext}
            disabled={currentIndex === questions.length - 1}
            className="d-flex align-items-center justify-content-center btn-circle ms-2"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        </div>

        <div className="text-end mt-5">
          <button type="button" className="btn-rejster py-2" onClick={handleSubmit}>
            إنهاء الامتحان
          </button>
        </div>
      </div>
    </Layout>
  );
}
